import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import RelatedServicesSkeleton from './RelatedServicesSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import LocalizedNavLink from './../../hooks/LocalizedNavLink'; 
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import { IMAGES_BASE_URL } from '../../config/constants';
import './RelatedServices.scss';

const RelatedServices = ({ services }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const { 
    data: relatedServicesJson, 
    loading: dataLoading, 
    error: dataError, 
    refetch 
  } = useCachedFetch('/data/locales/relatedServices.json');

  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      setHasError(true);
      setHasData(false);
      setIsLoading(false);
      return;
    }

    const servicesData = relatedServicesJson?.relatedServices || [];
   
    const filteredServices = servicesData.filter(service => 
      services.includes(service.id)
    );
    
    if (filteredServices && Array.isArray(filteredServices) && filteredServices.length > 0) {
      setHasData(true);
      setHasError(false);
    } else {
      setHasData(false);
      setHasError(true);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, relatedServicesJson, services]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const servicesData = useMemo(() => 
    relatedServicesJson?.relatedServices || [],
    [relatedServicesJson]
  );

  const allServices = useMemo(() => 
    servicesData.filter(service => services.includes(service.id)),
    [servicesData, services]
  );

  const mainContent = useMemo(() => ({
    contentAccent: {
      ua: "Пов'язані послуги",
      en: "Related Services", 
      ru: "Связанные услуги",
      pl: "Powiązane usługi",
      de: "Verwandte Dienstleistungen",
      es: "Servicios relacionados"
    }
  }), []);

  const sectionTitle = useMemo(() => 
    mainContent.contentAccent[currentLang] || mainContent.contentAccent.en,
    [mainContent, currentLang]
  );

  const getLocalizedText = useCallback((textObj) => {
    if (!textObj) return '';
    if (typeof textObj === 'object') {
      return textObj[currentLang] || textObj.en || '';
    }
    return textObj;
  }, [currentLang]);

  const localizedServices = useMemo(() => 
    allServices.map(service => ({
      ...service,
      localizedTitle: getLocalizedText(service.title),
      localizedDescription: getLocalizedText(service.description),
      altText: `${
        Array.isArray(getLocalizedText(service.title)) 
          ? getLocalizedText(service.title).join(' ') 
          : getLocalizedText(service.title)
      } icon`
    })),
    [allServices, getLocalizedText]
  );

  const hasValidData = useMemo(() => 
    !hasError && hasData && localizedServices.length > 0,
    [hasError, hasData, localizedServices.length]
  );

  if (isLoading) {
    return <RelatedServicesSkeleton />;
  }

  if (!hasValidData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  return (
    <div className='related__services__section'>
      <AnimatedOnScroll animation="fade-up" delay={0}>
      <h2 className='related__services__section__title'>
        {sectionTitle}
      </h2>
      </AnimatedOnScroll>
      <div className='related__services__section__wrapper'>
        {localizedServices.map((service, index) => {
          const titleArray = Array.isArray(service.localizedTitle) 
            ? service.localizedTitle 
            : [service.localizedTitle];

          return (
            <div className='services__related__links__item' key={service.id || index}>
              <AnimatedOnScroll animation="fade-up" delay={100}>
              <img 
                src={`${IMAGES_BASE_URL}${service.icon}`}
                alt={service.altText} 
                className="related__icon" 
                loading='lazy'
              />
              <LocalizedNavLink to={service.link} className='related__link__item'>
                {titleArray.map((line, lineIndex) => (
                  <h3 key={lineIndex} className="related__title__line">
                    {line}
                  </h3>
                ))}
              </LocalizedNavLink>
              <p className='services__related__link__text'>
                {service.localizedDescription}
              </p>
              </AnimatedOnScroll>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RelatedServices;